import { API_URLS } from '../config/apiConfig';
import { handleApiError } from '../config/errorHandling';
import { formatUAETime } from '../dateUtils';
import { addDays } from "date-fns";
import { getEnvironment } from "../filePathUtils";

export const fetchTableDataSU = async (
	idToken: string, 
	email: string, 
	latestTimestamp?: string,
	productionOnly: boolean = false
): Promise<string[]> => {
	console.log("fetchTableDataSU called with productionOnly:", productionOnly);
	if (!idToken || !email) {
		throw new Error("No idToken or user email available");
	}

	try {
		let allItems: any[] = [];
		let nextKey: { log_timestamp?: string; sub_month?: string } | null = null;
		let iterationCount = 0;
		const MAX_ITERATIONS = 10;
		
		const currentYearMonth = formatUAETime(new Date(), 'yyyy-MM');
		let currentQueryMonth = latestTimestamp ? 
			formatUAETime(new Date(latestTimestamp), 'yyyy-MM') : 
			currentYearMonth;
		
		const uniqueItems = new Set();
		

		const currentEnv = getEnvironment();
		
		do {
			const queryParams = new URLSearchParams({
				limit: '500'
			});

			if (nextKey) {
				console.log('Making request with:', {
					log_timestamp: nextKey.log_timestamp,
					sub_month: nextKey.sub_month
				});
				queryParams.append('start_date', nextKey.log_timestamp || '');
				queryParams.append('sub_month', nextKey.sub_month || '');
				queryParams.append('next_key', 
					typeof nextKey === 'string' ? nextKey : JSON.stringify(nextKey)
				);
			} else {
			    const tomorrow = addDays(new Date(), 1);
				const endDate = formatUAETime(tomorrow, "yyyy-MM-dd 23:59:59");
				console.log('Making initial request with:', {
					sub_month: currentQueryMonth,
					start_date: currentQueryMonth === formatUAETime(new Date(latestTimestamp || new Date()), 'yyyy-MM') 
						? formatUAETime(new Date(latestTimestamp || new Date()), "yyyy-MM-dd HH:mm:ss")
						: `${currentQueryMonth}-01 00:00:00`,
					end_date: endDate
				});
				queryParams.append('sub_month', currentQueryMonth);
				
				if (currentQueryMonth === formatUAETime(new Date(latestTimestamp || new Date()), 'yyyy-MM')) {
					queryParams.append('start_date', formatUAETime(new Date(latestTimestamp || new Date()), "yyyy-MM-dd HH:mm:ss"));
				} else {
					queryParams.append('start_date', `${currentQueryMonth}-01 00:00:00`);
				}
				
				queryParams.append('end_date', endDate);
			}
            queryParams.append('env', currentEnv);
			console.log('Query params:', queryParams.toString());
			const apiResponse = await fetch(`${API_URLS.DynamoDB_TABLE_DATA}?${queryParams}`, {
				headers: {
					'Authorization': `${idToken}`,
					'Content-Type': 'application/json',
				},
			});

			if (!apiResponse.ok) {
				throw new Error(`HTTP error! status: ${apiResponse.status}`);
			}

			const apiData = await apiResponse.json();

			if (Array.isArray(apiData.items)) {
				apiData.items.forEach((item: any) => {
					// Only keep items matching the current environment
					if (item.env !== currentEnv) {
						console.log(`Filtering out item not matching env:`, item.submission_id, item.env);
						return;
					}
					console.log(`Keeping item for env ${currentEnv}:`, item.submission_id, item.env);
					const uniqueKey = `${item.submission_id}-${item.sub_month}-${item.log_timestamp}`;
					if (!uniqueItems.has(uniqueKey)) {
						uniqueItems.add(uniqueKey);
						allItems.push(item);
					}
				});
			} else {
				break;
			}

			nextKey = apiData.next_key ? 
				(typeof apiData.next_key === 'string' ? JSON.parse(apiData.next_key) : apiData.next_key) 
				: null;

			iterationCount++;

			if (iterationCount >= MAX_ITERATIONS) {
				break;
			}
		} while (nextKey);

		return allItems;
	} catch (error) {
		return handleApiError(error, "Error fetching table data");
	}
};
